import axios, { Axios } from 'axios';
import { AuthError } from './errors';
import { Link, ReadOnlyProps, User } from './types';
import { maskPassword } from './utils';


export interface IMBPClient {
  setHost(host: string): void;
  setToken(token: string): void;
  login(username: string, password: string): Promise<User>;
  logout(): void;
  getUser(id: number): Promise<User>;
  verifySession(): Promise<boolean>;
  getAllUsers(): Promise<User[]>;
  createUser(user: Omit<User, 'id' | ReadOnlyProps>): Promise<User>;
  updateUser(user: Omit<User, ReadOnlyProps>): Promise<User>;
  getUserLinks(user: User): Promise<Link[]>;
  deleteUserLinks(user: User): Promise<User>;
  addLink(user: User, link: Omit<Link, 'id' | 'userId' | ReadOnlyProps>): Promise<Link>;
  updateLink(user: User, link: Omit<Link, ReadOnlyProps>): Promise<Link>;
}

export class MBPClient implements IMBPClient {
  private token: string | null = null;
  private isAuthenticated: boolean = false;
  private client: Axios;

  constructor(private host: string) {
    this.client = axios.create({
      baseURL: host,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  public setHost(host: string): void {
    this.host = host;
    this.refreshAxiosBaseUrl();
  }

  public setToken(token: string): void {
    this.token = token
    this.setAuthorizationHeader(token)
  }

  public getToken(): string | null {
    return this.token
  }

  public logout(): void {
    this.setToken('')
    this.isAuthenticated = false
  }

  private refreshAxiosBaseUrl(): void {
    this.client.defaults.baseURL = this.host;
  }

  private setAuthorizationHeader(token: string): void {
    this.client.defaults.headers.common.Authorization = `Bearer ${token}`;
    this.token = token;
    this.isAuthenticated = true;
  }

  public async login(username: string, password: string): Promise<User> {
    try {
      const response = await this.client.post('/auth/login', { username, password });
      const { user, token } = response.data.data;

      this.setAuthorizationHeader(token);
      
      return user as User;
    } catch (error) {
      throw new AuthError('Could not authenticate user.', { username, password: maskPassword(password) });
    }
  }

  public async getAllUsers(): Promise<User[]> {
    this.ensureAuthenticated();
    const response = await this.client.get('/users');
    return response.data.data as User[];
  }

  public async getUser(id: number): Promise<User> {
    this.ensureAuthenticated();
    const response = await this.client.get(`/users/${id}`)
    return response.data.data
  }

  public async verifySession(): Promise<boolean> {
    try {
      const response = await this.client.get(`/users/me`)
    } catch {
      return false
    }
    return true
  }

  public async createUser(user: Omit<User, 'id' | ReadOnlyProps>): Promise<User> {
    this.ensureAuthenticated();
    const response = await this.client.post('/users/create', user);
    return response.data.data;
  }

  public async updateUser(user: Omit<User, ReadOnlyProps>): Promise<User> {
    this.ensureAuthenticated();
    const response = await this.client.patch(`/users/${user.id}/update`, user);
    return response.data.data.user as User;
  }

  public async deleteUser(user: Omit<User, ReadOnlyProps>): Promise<User> {
    this.ensureAuthenticated();
    const response = await this.client.delete(`/users/${user.id}/delete`);
    return response.data.data.user as User;
  }

  public async getUserLinks(user: Omit<User, ReadOnlyProps>): Promise<Link[]> {
    this.ensureAuthenticated();
    const response = await this.client.get(`/users/${user.id}/links`);
    return response.data.data as Link[];
  }

  public async deleteUserLinks(user: Omit<User, ReadOnlyProps>): Promise<User> {
    this.ensureAuthenticated();
    const response = await this.client.delete(`/users/${user.id}/links/delete`)
    return response.data.data
  }

  public async addLink(user: Omit<User, ReadOnlyProps>, link: Omit<Link, 'id' | 'userId' | ReadOnlyProps>): Promise<Link> {
    this.ensureAuthenticated();
    const response = await this.client.post(`/users/${user.id}/links/create`, { userId: user.id, ...link });
    return response.data.data as Link;
  }

  public async updateLink(user: User, link: Omit<Link, ReadOnlyProps>): Promise<Link> {
    this.ensureAuthenticated();
    const response = await this.client.patch(`/users/${user.id}/links/${link.id}/update`, link);
    return response.data.data as Link;
  }

  private ensureAuthenticated(): void {
    if (!this.isAuthenticated || !this.token) {
      throw new AuthError('User is not authenticated.');
    }
  }
}
